var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { Button, VideoButton } from "../../../../button";
import { Link } from "../../../../link";
import { AspectRatio } from "../../../../aspect-ratio";
import warning from "warning";
import { EventType } from "../../Tile.types";
import { HeadlineWithShortTeaserContainer, StyledParagraph, StyledWrapper, StyledLinkWrapper, StyledTypo, StyledTitle, } from "./Tleb1.styles";
import { BorderLayer, StyledLink } from "../common/Common.styles";
import { ImageContainer } from "./ImageWrapper.styles";
import { GdprContainer, GdpgButtonWrapper } from "./gdprContainer.styles";
import { Image } from "../image/image";
// utils
import { makeDataTestIdGetter } from "../../../../utils";
function evaluateProps({ headlineFontSize, customBackgroundColor, buttonStyleType, linkStyleType, }) {
    warning(!headlineFontSize || ["h4", "h5", "h6", "h7"].includes(headlineFontSize), `headlineFontSize should be one of the following values: 'h4', 'h5', 'h6', 'h7'.`);
    warning(!customBackgroundColor ||
        ["white", "light"].includes(customBackgroundColor), `customBackgroundColor should be one of the following values: 'white', 'light'`);
    warning(!buttonStyleType ||
        ["primary", "secondary", "flat", "flatHighlight"].includes(buttonStyleType), `buttonStyleType should be one of the following values: 'primary', 'secondary', 'flat', 'flatHighlight'.`);
    warning(!linkStyleType || ["primary", "secondary", "light"].includes(linkStyleType), `linkStyleType should be one of the following values: "primary", "secondary", "light".`);
}
export function Tleb1(_a) {
    var { title, paragraph, children, customParagraph, fontSize = "p2", image, imageTitle, gap, link, altText, buttonText, gdprConsent, gdprConsentText, gdprConsentButtonText, gdprConsentLink, onEvent, onLinkClick, customBackgroundColor, size = "s", headlineFontSize = "h5", imageAspectRatio = "16:9", showPlayButton, linkStyleType = "primary", buttonStyleType = "flatHighlight", noBorder = false, buttonSymbol = "Download", fallbackImg, backgroundColor, renderHeadline, renderButton, titlePreviewId = "#st_title", dataTestIdPrefix } = _a, rest = __rest(_a, ["title", "paragraph", "children", "customParagraph", "fontSize", "image", "imageTitle", "gap", "link", "altText", "buttonText", "gdprConsent", "gdprConsentText", "gdprConsentButtonText", "gdprConsentLink", "onEvent", "onLinkClick", "customBackgroundColor", "size", "headlineFontSize", "imageAspectRatio", "showPlayButton", "linkStyleType", "buttonStyleType", "noBorder", "buttonSymbol", "fallbackImg", "backgroundColor", "renderHeadline", "renderButton", "titlePreviewId", "dataTestIdPrefix"]);
    const gdprContainerRef = useRef(null);
    const [gdprContainerWidth, setGdprContainerWidth] = useState(0);
    const [hovered, setHovered] = useState(false);
    const [focused, setFocused] = useState(false);
    const getDataTestId = makeDataTestIdGetter(dataTestIdPrefix);
    const resizeHandler = () => {
        if (gdprContainerRef.current) {
            const { width } = gdprContainerRef.current.getBoundingClientRect();
            setGdprContainerWidth(width);
        }
    };
    useEffect(() => {
        evaluateProps({
            buttonStyleType,
            customBackgroundColor,
            headlineFontSize,
            linkStyleType,
        });
        resizeHandler();
        window.addEventListener("resize", resizeHandler);
        return () => window.removeEventListener("resize", resizeHandler);
    }, []);
    function handleEvent(event) {
        if (typeof onEvent === "function") {
            onEvent(event);
        }
    }
    function getHeadlineFontSize() {
        if (["h4", "h5", "h6", "h7"].includes(headlineFontSize)) {
            return headlineFontSize;
        }
        return "h5";
    }
    function getCustomBackgroundColor() {
        if (["white", "light"].includes(customBackgroundColor)) {
            return customBackgroundColor;
        }
        return "white";
    }
    function getButtonStyleType() {
        if (["primary", "secondary", "flat", "flatHighlight"].includes(buttonStyleType)) {
            return buttonStyleType;
        }
        return "flatHighlight";
    }
    function getLinkStyleType() {
        if (["primary", "secondary", "light"].includes(linkStyleType)) {
            return linkStyleType;
        }
        return "primary";
    }
    function renderVideoButton() {
        if (!showPlayButton) {
            return _jsx(_Fragment, {});
        }
        return (_jsx(VideoButton, { onClick: (e) => {
                e.stopPropagation();
                handleEvent({ type: EventType.PLAY_BUTTON_CLICK });
            }, size: "small", "data-testid": getDataTestId("tleb1-videobutton-input") }));
    }
    function handleMediaClick(e) {
        if (showPlayButton) {
            handleEvent({ type: EventType.PLAY_BUTTON_CLICK });
            return;
        }
        if (onLinkClick) {
            onLinkClick(e);
        }
        else if (link) {
            link.onClick(e);
        }
        else
            return;
    }
    function renderMediaWrapper() {
        return (_jsx(AspectRatio, Object.assign({ ratio: imageAspectRatio }, { children: _jsxs(ImageContainer, Object.assign({ className: "image-container", hovered: hovered, onClick: (e) => handleMediaClick(e) }, { children: [children ? (children) : (_jsx(Image, { title: imageTitle, alt: altText, src: image, fallbackSrc: fallbackImg, fallbackBackgroundColor: backgroundColor })), renderVideoButton()] })) })));
    }
    function renderGdprConsent() {
        return (_jsxs(GdprContainer, Object.assign({ ref: gdprContainerRef, minHeight: (gdprContainerWidth * 9) / 16 }, { children: [_jsxs(StyledTypo, Object.assign({ variant: fontSize }, { children: [gdprConsentText, gdprConsentLink &&
                            gdprConsentLink.target &&
                            gdprConsentLink.text && (_jsx(Link, { standardFontSize: fontSize === "p2", onClick: () => handleEvent({ type: EventType.GDPR_LINK_CLICK }), inContext: true, stylingType: "inverted", hasArrow: true, href: gdprConsentLink.target, target: gdprConsentLink.target, text: gdprConsentLink.text, "data-testid": getDataTestId("tleb1-gdpq-link-input") }))] })), _jsx(GdpgButtonWrapper, { children: _jsx(Button, Object.assign({ onClick: () => handleEvent({ type: EventType.GDPR_BUTTON_CLICK }), buttonType: "button", stylingType: "flatInverted", "data-testid": getDataTestId("tleb1-gdpq-button-input") }, { children: gdprConsentButtonText })) })] })));
    }
    function renderParagraph() {
        if (customParagraph) {
            return customParagraph;
        }
        if (paragraph) {
            return (_jsx(StyledParagraph, Object.assign({ variant: fontSize }, { children: rest.previewId && rest.isPreview ? (_jsx("span", Object.assign({ "data-preview-id": "#st_text" }, { children: paragraph }))) : (paragraph) })));
        }
        return null;
    }
    function renderContent() {
        const isCustomButton = typeof renderButton === "function";
        const isCustomHeadline = typeof renderHeadline === "function";
        const isLinkValid = link && link.target && link.text;
        return (_jsxs(StyledWrapper, Object.assign({ widthBorder: !noBorder, onClick: (e) => {
                if (onLinkClick) {
                    onLinkClick(e);
                }
                else if (link) {
                    link.onClick(e);
                }
            } }, { children: [isCustomHeadline
                    ? renderHeadline()
                    : title && (_jsx(StyledTitle, Object.assign({ variant: getHeadlineFontSize() }, { children: rest.isPreview &&
                            (rest.previewId || titlePreviewId !== "#st_title") ? (_jsx("span", Object.assign({ "data-preview-id": titlePreviewId }, { children: title }))) : (title) }))), _jsx(_Fragment, { children: renderParagraph() }), isCustomButton
                    ? renderButton()
                    : buttonText && (_jsx(StyledLinkWrapper, { children: _jsx(Button, Object.assign({ onClick: (e) => {
                                e.stopPropagation();
                                handleEvent({ type: EventType.BUTTON_CLICK });
                            }, buttonType: "button", stylingType: getButtonStyleType(), symbol: buttonSymbol, "data-testid": getDataTestId("tleb1-gdpq-button-input") }, { children: buttonText })) })), !buttonText && !isCustomButton && isLinkValid && (_jsx(StyledLink, { onClick: (e) => {
                        if (link.onClick) {
                            e.stopPropagation();
                            link.onClick(e);
                        }
                    }, fontSize: fontSize, hasArrow: typeof link.hasArrow !== undefined ? link.hasArrow : true, stylingType: getLinkStyleType(), text: link.text, target: link.target, alignByContent: "left", windowTarget: link.windowTarget, "data-testid": getDataTestId("tleb1-link-input") }))] })));
    }
    return (_jsxs(HeadlineWithShortTeaserContainer, Object.assign({ customBackgroundColor: getCustomBackgroundColor(), size: size, gap: gap, tabIndex: 0, "data-testid": getDataTestId("tleab1-input"), onMouseDown: () => setFocused(false), onKeyUp: () => setFocused(true), onBlur: () => setFocused(false), onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), focused: focused }, rest, { children: [gdprConsent ? renderGdprConsent() : renderMediaWrapper(), renderContent(), _jsx(BorderLayer, { className: "border-layer" })] })));
}
